import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import { useLocation } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, X } from "lucide-react";
import { projects } from "../data/projects";

const transition = { duration: 0.8, ease: [0.4, 0, 0.2, 1] as const };

const cardVariants = {
  active: {
    x: "0%",
    y: "0%",
    scale: 1,
    opacity: 1,
    filter: "blur(0px)",
    zIndex: 30,
  },
  right: {
    x: "100%",
    y: "0%",
    scale: 0.65,
    opacity: 0.8,
    filter: "blur(0px)",
    zIndex: 20,
  },
  left: {
    x: "-100%",
    y: "0%",
    scale: 0.65,
    opacity: 0.8,
    filter: "blur(0px)",
    zIndex: 20,
  },
  back: {
    x: "0%",
    y: "0%",
    scale: 0.4,
    opacity: 0,
    filter: "blur(10px)",
    zIndex: 10,
  },
};

export default function Work() {
  const location = useLocation();
  const isHome = location.pathname === "/";
  const [active, setActive] = useState(0);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [expandOrigin, setExpandOrigin] = useState<DOMRect | null>(null);
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);
  const [isSwiping, setIsSwiping] = useState(false);

  const wheelLockRef = useRef(false);
  const wheelAccumulatorRef = useRef(0);
  const wheelTimerRef = useRef<NodeJS.Timeout | null>(null);
  const touchLockRef = useRef(false);

  const minSwipeDistance = 40;

  const next = useCallback(() => {
    setActive((prev) => (prev + 1) % projects.length);
  }, []);

  const prev = useCallback(() => {
    setActive((prev) => (prev - 1 + projects.length) % projects.length);
  }, []);

  const goTo = useCallback((i: number) => {
    setActive(i);
    setExpandedIndex(null);
  }, []);

  // Trackpad 2-finger horizontal scroll listener (desktop & laptop)
  useEffect(() => {
    const sectionEl = document.getElementById("work");
    if (!sectionEl) return;

    const handleWheel = (e: WheelEvent) => {
      // Intercept horizontal trackpad movement to prevent browser history navigation
      if (Math.abs(e.deltaX) > 0 && Math.abs(e.deltaX) >= Math.abs(e.deltaY)) {
        e.preventDefault();

        if (wheelLockRef.current) return;

        wheelAccumulatorRef.current += e.deltaX;

        if (Math.abs(wheelAccumulatorRef.current) > 40) {
          if (wheelAccumulatorRef.current > 0) {
            next();
          } else {
            prev();
          }
          wheelLockRef.current = true;
          wheelAccumulatorRef.current = 0;

          if (wheelTimerRef.current) clearTimeout(wheelTimerRef.current);
          wheelTimerRef.current = setTimeout(() => {
            wheelLockRef.current = false;
            wheelAccumulatorRef.current = 0;
          }, 750);
        }
      } else {
        wheelAccumulatorRef.current = 0;
      }
    };

    sectionEl.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      sectionEl.removeEventListener("wheel", handleWheel);
      if (wheelTimerRef.current) clearTimeout(wheelTimerRef.current);
    };
  }, [next, prev]);

  // Touch handlers for mobile/tablet finger swipes
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart({
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    });
    setIsSwiping(false);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStart) return;
    const deltaX = touchStart.x - e.touches[0].clientX;
    const deltaY = touchStart.y - e.touches[0].clientY;

    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
      setIsSwiping(true);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStart || touchLockRef.current) {
      setTouchStart(null);
      return;
    }
    const deltaX = touchStart.x - e.changedTouches[0].clientX;
    const deltaY = touchStart.y - e.changedTouches[0].clientY;

    if (Math.abs(deltaX) > minSwipeDistance && Math.abs(deltaX) > Math.abs(deltaY)) {
      touchLockRef.current = true;
      if (deltaX > 0) {
        next();
      } else {
        prev();
      }
      setTimeout(() => {
        touchLockRef.current = false;
      }, 450);
    }
    setTouchStart(null);
  };

  // Close expanded on Escape
  useEffect(() => {
    if (expandedIndex === null) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setExpandedIndex(null);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [expandedIndex]);

  const currentProject = projects[active];
  const tags = Array.isArray(currentProject.tag)
    ? currentProject.tag
    : [currentProject.tag];

  return (
    <section
      className={`relative w-full overflow-hidden text-white font-sans bg-transparent ${
        isHome ? "py-6 md:py-14" : "pt-28 pb-24 min-h-screen"
      }`}
      id="work"
      style={{ overscrollBehaviorX: "none" }}
    >
      {/* ── Bebas Neue ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&display=swap');
      `}</style>

      {/* ── Film Grain ── */}
      <div
        className="absolute inset-0 z-[5] opacity-20 pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* ── 1. Section Header ── */}
      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 relative z-30 mb-5 md:mb-12 pointer-events-auto">
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="font-Montserrat text-xs tracking-widest uppercase text-indigo-600 dark:text-indigo-300/70 mb-6 flex items-center gap-4"
        >
          <div className="w-4 h-[1px] bg-indigo-500/50" />
          <span>Our Portfolio</span>
        </motion.div>

        <motion.h2
          initial={{ y: 80, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
          className="font-Montserrat text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight text-black dark:text-white leading-[1.15] pb-1 md:pb-2"
        >
          Selected{" "}
          <span className="inline-block text-transparent bg-clip-text bg-gradient-to-r from-black dark:from-white via-indigo-600 dark:via-indigo-200 to-indigo-500/50 italic font-serif pb-1 md:pb-2 pr-2">
            Work
          </span>
        </motion.h2>
      </div>

      {/* ── 2. Carousel Depth System (Stage) ── */}
      <div
        className="relative w-full h-[320px] sm:h-[420px] md:h-[65vh] min-h-[300px] md:min-h-[520px] flex items-center justify-center overflow-hidden touch-pan-y"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Left Arrow */}
        <div className="absolute inset-y-0 left-3 md:left-8 flex items-center z-50 pointer-events-none">
          <button
            onClick={() => next()}
            type="button"
            className="w-10 h-10 md:w-16 md:h-16 rounded-full bg-white/5 border border-white/10 backdrop-blur-md flex items-center justify-center hover:scale-[1.1] hover:bg-white/10 active:scale-95 transition-all duration-300 pointer-events-auto cursor-pointer text-white/50 hover:text-white"
            aria-label="Previous project"
          >
            <ArrowLeft className="w-4 h-4 md:w-7 md:h-7" strokeWidth={1.5} />
          </button>
        </div>

        {/* Right Arrow */}
        <div className="absolute inset-y-0 right-3 md:right-8 flex items-center z-50 pointer-events-none">
          <button
            onClick={() => prev()}
            type="button"
            className="w-10 h-10 md:w-16 md:h-16 rounded-full bg-white/5 border border-white/10 backdrop-blur-md flex items-center justify-center hover:scale-[1.1] hover:bg-white/10 active:scale-95 transition-all duration-300 pointer-events-auto cursor-pointer text-white/50 hover:text-white"
            aria-label="Next project"
          >
            <ArrowRight className="w-4 h-4 md:w-7 md:h-7" strokeWidth={1.5} />
          </button>
        </div>

        {/* Carousel 3D Cards */}
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none perspective-[1200px]"
        >
          {projects.map((project, i) => {
            const offset = (i - active + projects.length) % projects.length;
            let pos: "active" | "right" | "left" | "back" = "back";
            if (offset === 0) pos = "active";
            else if (offset === 1) pos = "right";
            else if (offset === projects.length - 1) pos = "left";

            return (
              <motion.div
                key={project.title}
                variants={cardVariants}
                initial={false}
                animate={pos}
                transition={transition}
                onClick={(e) => {
                  if (isSwiping) return;
                  if (pos === "active") {
                    const rect = (
                      e.currentTarget as HTMLElement
                    ).getBoundingClientRect();
                    setExpandOrigin(rect);
                    setExpandedIndex(active);
                    return;
                  }
                  if (pos === "left") next();
                  if (pos === "right") prev();
                }}
                className="kivas-work-card absolute overflow-hidden rounded-xl md:rounded-2xl border border-white/5 shadow-[0_0_80px_rgba(0,0,0,0.4)] pointer-events-auto cursor-pointer select-none touch-pan-y"
                data-project={project.title}
                style={{
                  aspectRatio: "16/10",
                  transformStyle: "preserve-3d",
                }}
              >
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover select-none"
                  draggable={false}
                />
              </motion.div>
            );
          })}
        </motion.div>
      </div>

      {/* ── 3. Bottom Project Details & View Project CTA (Below Images) ── */}
      <div className="w-full px-4 md:px-12 lg:px-16 mt-4 md:mt-10 relative z-40">
        <div className="grid grid-cols-1 md:grid-cols-3 items-end gap-3 md:gap-6 w-full">
          {/* Column 1 (Left): Tags & Project Title */}
          <div className="w-full text-center md:text-left pointer-events-auto min-w-0">
            <AnimatePresence mode="popLayout" initial={false}>
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15, filter: "blur(5px)" }}
                transition={transition}
                className="flex flex-col items-center md:items-start min-w-0"
              >
                {/* Tags */}
                <div className="flex flex-wrap gap-1.5 md:gap-2 mb-1.5 md:mb-2 justify-center md:justify-start">
                  {tags.map((t) => (
                    <span
                      key={t}
                      className="rounded-full px-2.5 py-0.5 md:px-3 md:py-1 font-Montserrat text-[9px] md:text-[10px] font-medium uppercase tracking-widest border border-white/25 text-white/80 bg-white/[0.08] shrink-0"
                    >
                      {t}
                    </span>
                  ))}
                </div>

                {/* Title */}
                <h2
                  className="font-bold uppercase tracking-tight leading-none text-white truncate max-w-full"
                  style={{
                    fontFamily: "'Bebas Neue', sans-serif",
                    fontSize: "clamp(1.5rem, 2.5vw, 2.6rem)",
                    letterSpacing: "0.03em",
                  }}
                  title={currentProject.title}
                >
                  {currentProject.title}
                </h2>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Column 2 (Center): Pagination Progress Bars (Mathematically Locked to Grid Center) */}
          <div className="flex items-center justify-center gap-2 md:gap-3 pointer-events-auto py-1 md:py-2">
            {projects.map((project, i) => (
              <button
                key={project.title}
                type="button"
                onClick={() => goTo(i)}
                className="relative flex flex-col items-center gap-1 group py-1 md:py-2 cursor-pointer shrink-0"
                aria-label={`Go to slide ${i + 1}`}
              >
                <div
                  className="h-1.5 rounded-full transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]"
                  style={{
                    width: i === active ? "48px" : "16px",
                    backgroundColor:
                      i === active
                        ? (project.accent ?? "#6366f1")
                        : "rgba(255,255,255,0.2)",
                  }}
                />
              </button>
            ))}
          </div>

          {/* Column 3 (Right): View Project CTA (Mathematically Anchored to Far Right) */}
          <div className="pointer-events-auto flex items-center justify-center md:justify-end w-full">
            <button
              onClick={() => {
                const cardEl = document.querySelector(
                  `[data-project="${currentProject.title}"]`,
                );
                if (cardEl) {
                  const rect = cardEl.getBoundingClientRect();
                  setExpandOrigin(rect);
                }
                setExpandedIndex(active);
              }}
              type="button"
              className="group flex items-center gap-2 md:gap-3 hover:opacity-75 transition-opacity duration-300 cursor-pointer shrink-0"
            >
              <span
                style={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  fontSize: "clamp(1.8rem, 4vw, 3.5rem)",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  lineHeight: 1,
                  position: "relative",
                  top: "2px",
                }}
                className="text-white whitespace-nowrap"
              >
                View Project
              </span>
              <ArrowUpRight
                className="transform group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] text-white"
                style={{
                  width: "clamp(1.3rem, 2.5vw, 2.8rem)",
                  height: "clamp(1.3rem, 2.5vw, 2.8rem)",
                }}
              />
            </button>
          </div>
        </div>
      </div>

      {/* ── Expanded Project Overlay ── */}
      <AnimatePresence>
        {expandedIndex !== null && expandOrigin && (
          <motion.div
            key="expanded-overlay"
            className="fixed inset-0 z-[100]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div
              className="absolute inset-0 bg-black/80 backdrop-blur-xl"
              onClick={() => setExpandedIndex(null)}
            />

            <motion.div
              className="absolute overflow-hidden"
              initial={{
                top: expandOrigin.top,
                left: expandOrigin.left,
                width: expandOrigin.width,
                height: expandOrigin.height,
                borderRadius: "16px",
              }}
              animate={{
                top: "5vh",
                left: "5vw",
                width: "90vw",
                height: "90vh",
                borderRadius: "24px",
              }}
              exit={{
                top: expandOrigin.top,
                left: expandOrigin.left,
                width: expandOrigin.width,
                height: expandOrigin.height,
                borderRadius: "16px",
              }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              style={{ transformStyle: "preserve-3d" }}
            >
              {(() => {
                const project = projects[expandedIndex];
                const expandedTags = Array.isArray(project.tag)
                  ? project.tag
                  : [project.tag];
                return (
                  <>
                    <img
                      src={project.image}
                      alt={project.title}
                      className="absolute inset-0 w-full h-full object-cover opacity-60 blur-[3px]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />

                    <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12 z-10">
                      <div className="flex flex-wrap gap-2 mb-4">
                        {expandedTags.map((t) => (
                          <span
                            key={t}
                            className="rounded-full px-3 py-1.5 font-Montserrat text-[10px] font-medium uppercase tracking-widest border border-white/20 text-white bg-black/50 backdrop-blur-md"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                      <h2
                        className="font-bold uppercase tracking-tight leading-none text-white mb-3"
                        style={{
                          fontFamily: "'Bebas Neue', sans-serif",
                          fontSize: "clamp(2rem, 5vw, 4rem)",
                          letterSpacing: "0.03em",
                        }}
                      >
                        {project.title}
                      </h2>
                      <p className="text-white/85 font-Montserrat text-sm md:text-base max-w-2xl leading-relaxed text-justify">
                        {project.desc}
                      </p>
                    </div>

                    <button
                      onClick={() => setExpandedIndex(null)}
                      type="button"
                      className="absolute top-4 right-4 z-20 w-12 h-12 rounded-full bg-white/10 border border-white/20 backdrop-blur-md flex items-center justify-center hover:bg-white/20 active:scale-95 transition-all duration-300 text-white"
                    >
                      <X className="w-5 h-5" strokeWidth={2} />
                    </button>
                  </>
                );
              })()}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}